
import { Component, OnInit, ViewEncapsulation, inject, input, signal, computed, Signal, WritableSignal } from '@angular/core';
import { MDDialogModule, MD_DIALOG_REF, MDDialogRef, MDDropDownComponent, IMDDropDownOption } from '@melodic-ui/components';
import { FormsModule } from '@angular/forms';
import { LoaderComponent } from '../loader/loader.component';
import { IEnrollment } from '../../api/mylb/interfaces/ienrollment-summary.interface';
import { AdminPeopleService } from '../../api/mylb/services/admin-people.service';
import { IInstructorGridItem } from '../../api/mylb/interfaces/iinstructor-grid-item.interface';
import { FormFieldComponent } from '../form/form-field/form-field.component';
import { EnrollmentMemberService } from '@lb/api/mylb/services/enrollment-member.service';
// Type-only: the service imports this component, so a value import here would close the cycle.
import type { ILessonInstructorContext } from './substitute-instructor-dialog.service';

/**
 * Longest instructor label rendered in the dropdown.
 *
 * This is what controls how wide the open options panel is. The panel is a CDK overlay portalled to
 * the body, so it is not a descendant of the dialog and no stylesheet here can cap its width — it is
 * sized by its widest option. An instructor teaching several languages produces a label long enough
 * to stretch the panel across the whole viewport, so the label is capped at the source instead.
 *
 * Sized to fit the dialog rather than to a guess: the widest label that still fits is around 55
 * characters, and the trailing ellipsis makes it obvious the option continues.
 */
const INSTRUCTOR_OPTION_MAX_LENGTH = 55;

/**
 * Shortens to `max` characters including the ellipsis, breaking at the last space so a label never
 * ends mid-word. Falls back to a hard cut when there is no space to break on.
 */
function truncate(value: string, max: number): string {
	if (value.length <= max) {
		return value;
	}

	const cut = value.slice(0, max - 1);
	const lastSpace = cut.lastIndexOf(' ');

	// Only break on a space that leaves something readable — near the start, a hard cut is better
	// than a two-character label. The trailing trim drops the comma left behind when the break lands
	// between two languages, so it reads "… (ESL (1A - 6B)…" rather than "… (ESL (1A - 6B),…".
	const kept = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;

	return `${kept.replace(/[\s,]+$/, '')}…`;
}

@Component({
	selector: 'lb-substitute-instructor-dialog',
	standalone: true,
	imports: [FormsModule, MDDialogModule, MDDropDownComponent, LoaderComponent, FormFieldComponent],
	templateUrl: './substitute-instructor-dialog.component.html',
	styleUrl: './substitute-instructor-dialog.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class SubstituteInstructorDialogComponent implements OnInit {
	private _dialogRef: MDDialogRef = inject(MD_DIALOG_REF);
	private _peopleService = inject(AdminPeopleService);
	private _enrollmentMemberService = inject(EnrollmentMemberService);

	enrollment = input.required<IEnrollment>();
	lessonIDs = input<number[]>([]);

	/**
	 * Who teaches the lesson(s) being changed right now. Null when the caller could not say, in which
	 * case the enrollment's instructor is the best available answer — but it is a fallback, not the
	 * same fact: a lesson already covered by a substitute has an instructor its enrollment does not
	 * know about.
	 */
	lessonInstructor = input<ILessonInstructorContext | null>(null);

	// State signals
	loadingInstructors: WritableSignal<boolean> = signal<boolean>(false);
	loadingUpdate: WritableSignal<boolean> = signal<boolean>(false);
	instructors: WritableSignal<IInstructorGridItem[]> = signal<IInstructorGridItem[]>([]);
	selectedInstructorUserId: WritableSignal<number | null> = signal<number | null>(null);
	error: WritableSignal<string | null> = signal<string | null>(null);
	updateError: WritableSignal<string | null> = signal<string | null>(null);
	searchTerm = signal<string>('');

	/** Free text recorded on the enrollment's audit trail. Optional — the row auto-summarises without it. */
	reason: WritableSignal<string> = signal<string>('');

	languageId = computed<number | undefined>(() => {
		const enr = this.enrollment();
		return enr?.currentEnrollmentHistory?.languageID || enr?.program?.languageID || undefined;
	});

	/** The enrollment's own instructor — the one on the current history row. May not teach this lesson. */
	primaryInstructorUserId = computed<number | null>(() => this.enrollment().instructor?.instructorUserID ?? null);

	/** Who teaches the selected lesson(s) today, falling back to the enrollment's instructor. */
	currentInstructorUserId = computed<number | null>(() => this.lessonInstructor()?.userID ?? this.primaryInstructorUserId());

	currentInstructorName = computed(() => {
		// The lesson's own instructor wins. Reading the enrollment's here is what made a covered lesson
		// display the primary, so an admin returning it to the primary saw no change and the request
		// came back rejected as "already teaches this enrollment".
		const lessonInstructorName = this.lessonInstructor()?.fullName;
		if (lessonInstructorName) {
			return lessonInstructorName;
		}

		const instructor = this.enrollment().instructor;
		return instructor ? `${instructor.instructorFullName}` : 'None Assigned';
	});

	/** True when the lesson is currently covered by somebody other than the enrollment's instructor. */
	isCovered = computed(() => {
		const primary = this.primaryInstructorUserId();
		const current = this.currentInstructorUserId();
		return primary !== null && current !== null && primary !== current;
	});
	languageName = computed(() => this.enrollment().currentEnrollmentHistory?.languageName || this.enrollment().program?.languageName || '');

	filteredInstructorOptions: Signal<IMDDropDownOption[]> = computed(() => {
		const term = this.searchTerm().toLowerCase();
		const currentUserId = this.currentInstructorUserId();
		const primaryUserId = this.primaryInstructorUserId();

		return this.instructors()
			.filter((i) => !term || i.fullName.toLowerCase().includes(term))
			.map((i) => {
				const languages =
					i?.instructorLanguages
						?.map((l: any) => {
							const levelRange =
								l.instructorLanguageLevelMin &&
								l.instructorLanguageLevelMax &&
								l.instructorLanguageLevelMin !== l.instructorLanguageLevelMax
									? ` (${l.instructorLanguageLevelMin} - ${l.instructorLanguageLevelMax})`
									: l.instructorLanguageLevelMin
										? ` (${l.instructorLanguageLevelMin})`
										: '';
							return `${l.languageName}${levelRange}`;
						})
						.join(', ') ?? '';

				const fullLabel = `${i.fullName} (${languages})`;

				// Two suffixes, never both. Without them the list is a flat roster in which the person
				// who already has the lesson and the person the enrollment belongs to are indistinguishable
				// from everyone else — which is what makes picking the wrong one so easy.
				const suffix =
					i.userId === currentUserId
						? ' — currently teaching'
						: i.userId === primaryUserId
							? ' — enrollment instructor'
							: '';

				return {
					value: i.userId,
					label: `${truncate(fullLabel, INSTRUCTOR_OPTION_MAX_LENGTH)}${suffix}`,
					// The untruncated text stays searchable. searchTerms is matched but never rendered,
					// so shortening the label costs nothing when an admin types a language they can no
					// longer see — which is the only thing truncation would otherwise have taken away.
					searchTerms: fullLabel
				} as IMDDropDownOption;
			});
	});

	/**
	 * Selecting the person who already teaches the lesson. The server answers this with
	 * `lessons_already_with_primary`, but there is no reason to spend a round trip discovering that the
	 * chosen action is a no-op — the dropdown already says who it is.
	 */
	isNoOpSelection = computed(() => {
		const selected = this.selectedInstructorUserId();
		return selected !== null && selected === this.currentInstructorUserId();
	});

	/**
	 * True when confirming would hand the lesson back to the enrollment's own instructor. Worth naming
	 * because it is the opposite operation and the copy has to say so — the endpoint handles it, but an
	 * admin should not be told they are "assigning a substitute" while undoing one.
	 */
	isReturnToPrimary = computed(() => {
		const selected = this.selectedInstructorUserId();
		return selected !== null && selected === this.primaryInstructorUserId() && this.isCovered();
	});

	// Computed states for UI
	isFormDisabled = computed(() => this.loadingInstructors() || this.loadingUpdate());
	canConfirm = computed(() => !this.isFormDisabled() && this.selectedInstructorUserId() !== null && !this.isNoOpSelection());

	confirmLabel = computed(() => (this.isReturnToPrimary() ? 'Return to Instructor' : 'Confirm'));

	ngOnInit(): void {
		this.loadInstructors();
	}

	loadInstructors(): void {
		const langId = this.languageId();
		if (!langId) {
			this.error.set('No language found for enrollment.');
			return;
		}
		this.loadingInstructors.set(true);
		this.error.set(null);
		this._peopleService.getInstructors(true, langId).subscribe({
			next: (list) => {
				this.instructors.set(list || []);
				this.loadingInstructors.set(false);
			},
			error: (err) => {
				this.error.set(err?.error?.message || 'Failed to load instructors');
				this.loadingInstructors.set(false);
			}
		});
	}

	onInstructorChange(userId: unknown): void {
		this.selectedInstructorUserId.set(userId as number);
		// Clear any previous update errors when user changes selection
		this.updateError.set(null);
	}

	close(): void {
		this._dialogRef.close();
	}

	onReasonChange(reason: string): void {
		this.reason.set(reason);
	}

	confirm(): void {
		const userId = this.selectedInstructorUserId();
		if (userId == null || this.loadingUpdate()) return;

		// Clear any previous update errors
		this.updateError.set(null);
		this.loadingUpdate.set(true);

		const instructor = this.instructors().find((i) => i.userId === userId) || null;
		const reason = this.reason().trim();

		// One call to Enrollment.Api now does the whole assignment: the substitute's enrollment history
		// row, moving these lessons onto it, and the membership row that puts them on the roster and
		// into the enrollment's Team. Teams access used to be arranged by hand.
		this._enrollmentMemberService
			.assignSubstitute(this.enrollment().enrollmentID, {
				instructorUserID: userId,
				lessonIDs: this.lessonIDs(),
				reason: reason || undefined
			})
			.subscribe({
				next: () => {
					this.loadingUpdate.set(false);
					// Still the instructor, not the assignment result: callers re-render from it, and
					// nothing downstream needs the history/member IDs the endpoint also returns.
					this._dialogRef.close(instructor);
				},
				error: (err) => {
					this.loadingUpdate.set(false);
					console.error('Failed to assign substitute instructor:', err);

					// Set user-friendly error message
					const errorMessage = err?.error?.message || err?.message || 'Failed to assign substitute. Please try again.';
					this.updateError.set(errorMessage);
				}
			});
	}
}
